import { graphql, useStaticQuery } from "gatsby"
import { HelmetDatoCms, HelmetDatoCmsTags } from "gatsby-source-datocms"
import React, { useContext, useEffect, useRef } from "react"
import { PageContext } from "../../context/PageContext"
import useAlternatePaths from "../../hooks/useAlternatePaths"
import "../../styles/default.scss"
import PageFooter from "../organisms/pageFooter/pageFooter"
import PageHeader from "../organisms/pageHeader/PageHeader"
import * as styles from "./DefaultLayout.module.scss"

type DefaultLayoutProps = {
  mainMenu?: Queries.DatoMainNavTreeFragment | null
  seoMetaTags: HelmetDatoCmsTags
  footer?: Queries.DatoFooterFragment | null
  children: React.ReactNode
}

const SITE_URL = "https://www.harvia.com"
const WEBSITE_TITLE = "Harvia"

const DefaultLayout = (props: DefaultLayoutProps) => {
  // console.debug("Rendering DefaultLayout", props)
  const { locale, localePim } = useContext(PageContext)
  const alternates = useAlternatePaths()

  const data = useStaticQuery(graphql`
    query DefaultLayout {
      datoCmsSite {
        faviconMetaTags {
          tags
        }
      }
    }
  `)

  /** Daton seoMetaTags.tags tuottaa vääränlaisen og:locale -tagin jos Datossa käytössä yksiosainen locale.
   *  Poistetaan tai käytetään localePim:ä kunnes toimiva korjaus löytyy.
   *  Ei tiedossa paljonko tällä vaikutusta hakukoneiden toimintaan.
   * */
  const fixedTags =
    props.seoMetaTags?.tags &&
    props.seoMetaTags.tags
      .map(tag => {
        if (tag.attributes?.property !== "og:locale") return tag // ei og:locale tagi

        const lOrig = tag.attributes.content.split("_") // "sv_SV"

        if (lOrig[0] !== lOrig[1].toLowerCase()) return tag // alkuperäinen on oikeassa muodossa

        const lPim = localePim.split("-") // "sv-SE"

        // käytetään localePim:iä, koska sama kieli
        if (lPim[0] === lOrig[0]) {
          tag.attributes.content = lPim.join("_") // https://ogp.me/ => oikea formaatti: sv_SE
          return tag
        }

        // eri kieli ja mahdollisesti väärässä formaatissa, poistetaan koko tagi
        return null
      })
      .filter(Boolean)

  const seo = {
    tags: [...data.datoCmsSite.faviconMetaTags.tags, ...(fixedTags || [])],
  }

  // Set fallback locale version canonical url
  // Todo: Check Dato record for available locales, and set canonical ONLY if current locale is not available
  const curPage = alternates.find(alt => alt.current)
  const canonical =
    !!curPage?.locale?.fallbackLocales?.length &&
    alternates.find(alt => curPage.locale?.fallbackLocales && alt.locale?.dato === curPage.locale?.fallbackLocales[0])

  const cookiebotLifecycle = useRef<{
    consentDuringClick: CookiebotConsents | undefined
    eventFromButtonClick: boolean
  }>({
    consentDuringClick: undefined,
    eventFromButtonClick: false,
  })

  useEffect(() => {
    /**
     * Reload browser if any of the given consent level were withdrawn. Implementation is hacking around the lacking Cookiebot API so that it works for all browsers.
     */
    function CookiebotOnDeclineHandler() {
      if (!cookiebotLifecycle.current.eventFromButtonClick) return

      const consentDuringClick = cookiebotLifecycle.current.consentDuringClick
      const currentConsent = window.Cookiebot?.consent

      if (!(consentDuringClick && currentConsent)) return

      if (
        (consentDuringClick.marketing && !currentConsent.marketing) ||
        (consentDuringClick.preferences && !currentConsent.preferences) ||
        (consentDuringClick.statistics && !currentConsent.statistics)
      ) {
        window.location.reload()
      }
    }

    function clickHandler(e: MouseEvent) {
      const buttonId = (e.target as { id?: string | null })?.id
      if (
        // We want listen for clicks on both "Deny" and "Allow Selection" buttons
        buttonId === "CybotCookiebotDialogBodyButtonDecline" ||
        buttonId === "CybotCookiebotDialogBodyLevelButtonLevelOptinAllowallSelection"
      ) {
        cookiebotLifecycle.current = {
          eventFromButtonClick: true,
          consentDuringClick: window.Cookiebot?.consent ? { ...window.Cookiebot.consent } : undefined,
        }
      }
    }

    const body = document.getElementsByTagName("body")[0]

    body.addEventListener("click", clickHandler)
    window.addEventListener("CookiebotOnDecline", CookiebotOnDeclineHandler)
    window.addEventListener("CookiebotOnAccept", CookiebotOnDeclineHandler)
    return () => {
      body.removeEventListener("click", clickHandler)
      window.addEventListener("CookiebotOnDecline", CookiebotOnDeclineHandler)
      window.addEventListener("CookiebotOnAccept", CookiebotOnDeclineHandler)
    }
  })

  return (
    <>
      <HelmetDatoCms
        seo={seo}
        titleTemplate={`%s | ${WEBSITE_TITLE}`}
        defaultTitle={WEBSITE_TITLE}
        htmlAttributes={{
          lang: locale,
        }}>
        {canonical && canonical.path && <link rel="canonical" href={SITE_URL + canonical.path} />}
        {/* Sama sisältö eri kielillä: https://developers.google.com/search/docs/advanced/crawling/localized-versions */}
        {alternates.map(alt => {
          if (!alt.path || !alt.locale?.dato) return null
          return <link key={alt.path} rel="alternate" hrefLang={alt.locale.dato} href={SITE_URL + alt.path} />
        })}
        <link rel="alternate" hrefLang="x-default" href={SITE_URL} />
      </HelmetDatoCms>
      <PageHeader mainMenu={props.mainMenu} />

      <main className={styles.content}>{props.children}</main>
      {props.footer && <PageFooter {...props.footer} />}
    </>
  )
}

export default DefaultLayout
